import React, { useState, useRef, useEffect } from "react";
import { ChatFolder, ChatSession } from "../../../services/api/chatHistory.api";
import { useClickOutside } from "../../../hooks/useClickOutside";
import SessionItem from "./SessionItem";
import { FolderOption } from "./FolderPickerPopover";

interface FolderWithSessions extends ChatFolder {
  sessions: ChatSession[];
  isOpen: boolean;
  isLoading: boolean;
}

interface FolderItemProps {
  folder: FolderWithSessions;
  activeSessionId: string | null;
  folderOptions: FolderOption[];                              // for the move-to-folder picker inside sessions
  onToggle: (folderId: number) => void;
  onSessionClick: (sessionId: string) => void;
  onSessionRename: (sessionId: string, newName: string) => void;
  onSessionDelete: (sessionId: string) => void;
  onRenameFolder: (folderId: number, newName: string) => void;
  onDeleteFolder: (folderId: number) => void;
  onMoveSession: (sessionId: string, folderId: number | null) => void; // This is to move seesion to and from folders
}

const FolderItem: React.FC<FolderItemProps> = ({
  folder,
  activeSessionId,
  folderOptions,
  onToggle,
  onSessionClick,
  onSessionRename,
  onSessionDelete,
  onRenameFolder,
  onDeleteFolder,
  onMoveSession, // This is to move seesion to and from folders

}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(folder.chat_folder_name);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close context menu on outside click (the ⋯ button counts as inside, so it
  // can close the menu too)
  useClickOutside([menuRef, menuBtnRef], () => setMenuOpen(false), menuOpen);

  // Focus rename input when editing starts
  useEffect(() => {
    if (isEditing) inputRef.current?.select();
  }, [isEditing]);

  const commitRename = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== folder.chat_folder_name) {
      onRenameFolder(folder.id, trimmed);
    } else {
      setEditValue(folder.chat_folder_name);
    }
    setIsEditing(false);
  };

  return (
    <div className="folder-item">
      {/* Folder header row */}
      <div
        className="chat-item folder-header"
        onClick={() => !isEditing && onToggle(folder.id)}
        title={folder.chat_folder_name}
      >
        <i className={`fas ${folder.isOpen ? "fa-folder-open" : "fa-folder"}`} />

        {isEditing ? (
          <input
            ref={inputRef}
            className="chat-item-rename-input"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") {
                setEditValue(folder.chat_folder_name);
                setIsEditing(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span>{folder.chat_folder_name}</span>
        )}

        <i 
        className={`fas fa-chevron-${folder.isOpen ? "down" : "right"} folder-chevron`}
        />

        {/* ⋯ folder context menu */}
        <button
          ref={menuBtnRef}
          className="chat-item-menu-btn"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((o) => !o);
          }}
          title="More options"
        >
          <i className="fas fa-ellipsis-h" />
        </button>

        {menuOpen && (
          <div ref={menuRef} className="chat-item-dropdown" onClick={(e) => e.stopPropagation()}>
            <button
              className="menu-item"
              onClick={() => {
                setMenuOpen(false);
                setIsEditing(true);
              }}
            >
              <i className="fas fa-pencil" />
              <span>Rename</span>
            </button>
            <button
              className="menu-item menu-item--danger"
              onClick={() => {
                setMenuOpen(false);
                onDeleteFolder(folder.id);
              }}
            >
              <i className="fas fa-trash" />
              <span>Delete folder</span>
            </button>
          </div>
        )}
      </div>

      {/* Sessions inside this folder */}
      {folder.isOpen && (
        <div className="folder-sessions">
          {folder.isLoading ? (
            <div className="chat-item chat-item--muted">
              <i className="fas fa-spinner fa-spin" />
              <span>Loading…</span>
            </div>
          ) : folder.sessions.length === 0 ? (
            <div className="chat-item chat-item--muted">
              <i className="fas fa-inbox" />
              <span>No chats in this folder</span>
            </div>
          ) : (
            folder.sessions.map((s) => (
              <SessionItem
                key={s.session_id}
                session={s}
                isActive={s.session_id === activeSessionId}
                folders={folderOptions}
                onClick={onSessionClick}
                onRename={onSessionRename}
                onDelete={onSessionDelete}
                onMoveToFolder={(sessionId, folderId) => onMoveSession(sessionId, folderId)}
                // onRemoveFromFolder is passed here because these sessions ARE inside a folder
                onRemoveFromFolder={(sessionId) => onMoveSession(sessionId, null)}

              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default FolderItem;